import { Box, Card, makeStyles, Slider, Typography } from '@material-ui/core';
import React, { FC, useEffect, useState } from 'react';

import VolumeUp from '@material-ui/icons/VolumeUp';
import CloseIcon from '@material-ui/icons/Close';

import { audioService, currentMusicVolume, currentSoundVolume } from '../views/gameView/Game/hooks/audioService.service';
import { FormattedMessage } from 'react-intl';
import { SOUNDS, useAudio } from '../views/gameView/Game/hooks/useAudio';

const useStyles = makeStyles(() => ({
    root: {
        position: 'relative',
        width: '333px',
        maxHeight: '134px',
        right: '0px',
        padding: '18px'
    },
    container: {
        textAlign: 'end',
    },
    icon: {
        cursor: 'pointer',
        marginTop: '18px',
        color: 'white',
    },
    top: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '24px',
    },
    title: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '26px',
        letterSpacing: '0.46px',
    },
    row: {
        display: 'flex',
    },
    close: {
        cursor: 'pointer',
    },
    setting: {
        display: 'flex',
        alignItems: 'center'
    },
    sliderLabel: {
        width: '54px',
    },
    slider: {
        width: '211px',
        marginLeft: '12px'
    }
}));

interface AudioSettingsProps {
    isOpen?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
}

const AudioSettings: FC<AudioSettingsProps> = ({ isOpen, onClose, onOpen }) => {
    const classes = useStyles();
    const buttonClickAudio = useAudio(SOUNDS.BUTTON_CLICK, {});

    const [internalShow, setInternalShow] = useState<boolean>(false);

    const isControlled = isOpen !== undefined;
    const showSettings = isControlled ? isOpen : internalShow;

    const handleOpen = () => {
        buttonClickAudio.play();
        if (isControlled && onOpen) {
            onOpen();
        } else {
            setInternalShow(true);
        }
    };

    const handleClose = () => {
        buttonClickAudio.play();
        if (isControlled && onClose) {
            onClose();
        } else {
            setInternalShow(false);
        }
    };

    const [soundValue, setSoundValue] = useState(0);
    const [musicValue, setMusicValue] = useState(0);

    useEffect(() => {
        setSoundValue(currentSoundVolume);
        setMusicValue(currentMusicVolume);

        let soundSubscription = audioService.getNewSoundVolume().subscribe((data: any) => {
            setSoundValue(data.newVolume);
        });
        let musicSubscription = audioService.getNewMusicVolume().subscribe((newVolume: any) => {
            setMusicValue(newVolume);
        });

        let changedSubscription = audioService.getChangedByCode().subscribe((data: any) => {
            setMusicValue(data.newVolume as number);
        });

        return () => {
            soundSubscription.unsubscribe();
            musicSubscription.unsubscribe();
            changedSubscription.unsubscribe();
        };
    }, []);

    return (
        <Box className={classes.container}>
            <VolumeUp className={classes.icon} onClick={handleOpen} />
            {showSettings && (
                <Card className={classes.root}>
                    <Box className={classes.top}>
                        <Box className={classes.row}>
                            <VolumeUp color="action" style={{ marginRight: '10px' }} />
                            <Typography className={classes.title}>
                                <FormattedMessage id="shared.AudioSettings.volume" />
                            </Typography>
                        </Box>
                        <CloseIcon className={classes.close} color="inherit" onClick={handleClose} />
                    </Box>
                    <Box className={classes.setting}>
                        <Typography className={`${classes.title} ${classes.sliderLabel}`}>
                            <FormattedMessage id="shared.AudioSettings.sounds" />
                        </Typography>
                        <Box className={classes.slider}>
                            <Slider
                                value={soundValue ?? 0}
                                min={0}
                                max={1}
                                step={0.1}
                                onChange={(e, newValue) => setSoundValue(newValue as number)}
                                onChangeCommitted={(e, newValue) => audioService.changedSoundVolume(newValue as number)}
                                color="primary"
                            />
                        </Box>
                    </Box>
                    <Box className={classes.setting}>
                        <Typography className={`${classes.title} ${classes.sliderLabel}`}>
                            <FormattedMessage id="shared.AudioSettings.music" />
                        </Typography>
                        <Box className={classes.slider}>
                            <Slider
                                value={musicValue ?? 0}
                                min={0}
                                max={1}
                                step={0.1}
                                onChange={(e, newValue) => setMusicValue(newValue as number)}
                                onChangeCommitted={(e, newValue) => audioService.changedMusicVolume(newValue as number)}
                                color="primary"
                            />
                        </Box>
                    </Box>
                </Card>
            )}
        </Box>
    );
};

export default AudioSettings;
